import React, { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { setActiveLearner } from "@/lib/learner";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card } from "@/components/ui/card";
import { User, Users } from "lucide-react";

export default function LearnerPicker({ open, onOpenChange, onPicked }) {
  const [list, setList] = useState([]);
  const [name, setName] = useState("");
  const [filter, setFilter] = useState("");

  useEffect(() => {
    if (!open) return;
    api.get("/learners/public").then(({ data }) => setList(data)).catch(() => setList([]));
  }, [open]);

  const pick = (learner) => {
    setActiveLearner(learner);
    onPicked?.(learner);
    onOpenChange(false);
  };

  const submitGuest = () => {
    if (!name.trim()) return;
    pick({ id: null, name: name.trim(), department: "Guest" });
  };

  const filtered = list.filter((l) => l.name.toLowerCase().includes(filter.toLowerCase()));

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle className="font-display flex items-center gap-2">
            <Users size={18} className="text-blue-900"/> Who's learning?
          </DialogTitle>
        </DialogHeader>

        <div className="space-y-4">
          <p className="text-xs text-slate-500">
            Your scores will be saved against this name so administrators can track progress.
          </p>

          {list.length > 0 && (
            <div>
              <Label className="text-xs text-slate-600">Pick from roster</Label>
              <Input
                data-testid="learner-picker-search"
                placeholder="Type to filter…"
                value={filter}
                onChange={(e) => setFilter(e.target.value)}
                className="mt-1.5 mb-2"
              />
              <Card className="max-h-60 overflow-y-auto thin-scroll p-1 border-slate-200">
                {filtered.length === 0 ? (
                  <div className="px-3 py-4 text-center text-xs text-slate-500">No match</div>
                ) : filtered.map((l) => (
                  <button
                    key={l.id}
                    onClick={() => pick(l)}
                    data-testid={`pick-learner-${l.id}`}
                    className="w-full text-left px-3 py-2 rounded hover:bg-blue-50 transition-colors flex items-center gap-2"
                  >
                    <span className="w-7 h-7 rounded-full bg-blue-900 text-white flex items-center justify-center text-xs font-semibold">
                      {l.name.charAt(0).toUpperCase()}
                    </span>
                    <span className="flex-1 min-w-0">
                      <span className="block text-sm text-slate-900 truncate">{l.name}</span>
                      {l.department && <span className="block text-[10px] text-slate-500 truncate">{l.department}</span>}
                    </span>
                  </button>
                ))}
              </Card>
            </div>
          )}

          <div className="relative pt-1">
            <div className="absolute inset-0 flex items-center"><div className="w-full border-t border-slate-200"/></div>
            <div className="relative flex justify-center text-[10px] uppercase tracking-wider"><span className="bg-white px-2 text-slate-400">or</span></div>
          </div>

          <div>
            <Label className="text-xs text-slate-600">Continue as guest</Label>
            <div className="flex gap-2 mt-1.5">
              <Input
                data-testid="learner-picker-guest-name"
                placeholder="Your name"
                value={name}
                onChange={(e) => setName(e.target.value)}
                onKeyDown={(e) => e.key === "Enter" && submitGuest()}
              />
              <Button data-testid="learner-picker-guest-submit" onClick={submitGuest} className="bg-blue-900 hover:bg-blue-800 text-white shrink-0">
                <User size={14} className="mr-1.5"/> Continue
              </Button>
            </div>
          </div>
        </div>

        <DialogFooter />
      </DialogContent>
    </Dialog>
  );
}
